/**
 * レポート差分検出
 */

import type { Report, ReportElementUnion } from '../../../types/report.types';
import type { ReportId } from '../../../types/id.types';
import type {
  ReportDiff,
  ContentDiff,
  ContentLineDiff,
  ReportElementChange,
  PropertyChange,
} from './types';
import { hasChanges, diff, extractChangedPaths } from './diffService';

/**
 * レポートの差分を計算
 */
export function diffReport(original: Report, modified: Report): ReportDiff {
  const reportId = modified.id as ReportId;
  const reportName = modified.name;

  // 名前の変更
  const nameChanged = original.name !== modified.name;

  // 説明の変更
  const descriptionChanged = original.description !== modified.description;

  // コンテンツの差分
  const contentDiff = diffContent(original.content || '', modified.content || '');

  // 要素の差分
  const elementChanges = diffElements(original.elements || {}, modified.elements || {});

  // 設定の差分
  const settingsChanged = hasChanges(original.reportSettings, modified.reportSettings);
  const settingsChanges = settingsChanged
    ? getSettingsChanges(original.reportSettings, modified.reportSettings)
    : undefined;

  const hasDifferences =
    nameChanged ||
    descriptionChanged ||
    contentDiff.hasChanges ||
    elementChanges.length > 0 ||
    settingsChanged;

  return {
    reportId,
    reportName,
    hasDifferences,
    nameChanged,
    oldName: nameChanged ? original.name : undefined,
    newName: nameChanged ? modified.name : undefined,
    descriptionChanged,
    oldDescription: descriptionChanged ? original.description : undefined,
    newDescription: descriptionChanged ? modified.description : undefined,
    contentDiff,
    elementChanges,
    settingsChanged,
    settingsChanges,
  };
}

/**
 * Markdown本文の行単位差分を計算
 * LCS（最長共通部分列）ベースのシンプルなアルゴリズム
 */
export function diffContent(original: string, modified: string): ContentDiff {
  if (original === modified) {
    return {
      hasChanges: false,
      lines: [],
      addedLines: 0,
      removedLines: 0,
    };
  }

  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');

  // LCSを計算
  const lcs = computeLCS(originalLines, modifiedLines);

  // 差分を生成
  const lines: ContentLineDiff[] = [];
  let oi = 0; // originalのインデックス
  let mi = 0; // modifiedのインデックス
  let lineNumber = 1;
  let addedLines = 0;
  let removedLines = 0;

  for (const commonLine of lcs) {
    // originalで削除された行
    while (oi < originalLines.length && originalLines[oi] !== commonLine) {
      lines.push({
        type: 'removed',
        lineNumber: lineNumber++,
        content: originalLines[oi],
      });
      removedLines++;
      oi++;
    }

    // modifiedで追加された行
    while (mi < modifiedLines.length && modifiedLines[mi] !== commonLine) {
      lines.push({
        type: 'added',
        lineNumber: lineNumber++,
        content: modifiedLines[mi],
      });
      addedLines++;
      mi++;
    }

    // 共通行
    lines.push({
      type: 'unchanged',
      lineNumber: lineNumber++,
      content: commonLine,
    });
    oi++;
    mi++;
  }

  // 残りの削除行
  while (oi < originalLines.length) {
    lines.push({
      type: 'removed',
      lineNumber: lineNumber++,
      content: originalLines[oi],
    });
    removedLines++;
    oi++;
  }

  // 残りの追加行
  while (mi < modifiedLines.length) {
    lines.push({
      type: 'added',
      lineNumber: lineNumber++,
      content: modifiedLines[mi],
    });
    addedLines++;
    mi++;
  }

  // コンテキスト付きで差分を絞り込む（変更周辺の行のみ）
  const filteredLines = filterWithContext(lines, 5);

  return {
    hasChanges: true,
    lines: filteredLines,
    addedLines,
    removedLines,
  };
}

/**
 * LCS（最長共通部分列）を計算
 */
function computeLCS(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;

  // DP table
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // バックトラックしてLCSを取得
  const lcs: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}

/**
 * 差分行の周辺コンテキストを含めてフィルタリング
 */
function filterWithContext(lines: ContentLineDiff[], contextLines: number): ContentLineDiff[] {
  if (lines.length === 0) return [];

  // 変更がある行のインデックスを取得
  const changedIndices = new Set<number>();
  lines.forEach((line, index) => {
    if (line.type !== 'unchanged') {
      changedIndices.add(index);
    }
  });

  if (changedIndices.size === 0) return [];

  // コンテキスト込みで含める行を決定
  const includeIndices = new Set<number>();
  for (const index of changedIndices) {
    for (let i = Math.max(0, index - contextLines); i <= Math.min(lines.length - 1, index + contextLines); i++) {
      includeIndices.add(i);
    }
  }

  // フィルタリング
  return lines.filter((_, index) => includeIndices.has(index));
}

/**
 * レポート要素の差分を計算
 */
export function diffElements(
  original: Record<string, ReportElementUnion>,
  modified: Record<string, ReportElementUnion>
): ReportElementChange[] {
  const changes: ReportElementChange[] = [];
  const originalIds = Object.keys(original);
  const modifiedIds = Object.keys(modified);
  const originalSet = new Set(originalIds);
  const modifiedSet = new Set(modifiedIds);

  // 追加された要素
  for (const id of modifiedIds) {
    if (!originalSet.has(id)) {
      const element = modified[id];
      changes.push({
        elementId: id,
        type: 'added',
        elementType: element.type,
        elementName: getElementName(element),
      });
    }
  }

  // 削除された要素
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) {
      const element = original[id];
      changes.push({
        elementId: id,
        type: 'removed',
        elementType: element.type,
        elementName: getElementName(element),
      });
    }
  }

  // 変更された要素
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) continue;

    const originalElement = original[id];
    const modifiedElement = modified[id];

    if (hasChanges(originalElement, modifiedElement)) {
      const propertyChanges = getElementPropertyChanges(originalElement, modifiedElement);
      changes.push({
        elementId: id,
        type: 'modified',
        elementType: modifiedElement.type,
        elementName: getElementName(modifiedElement),
        propertyChanges,
      });
    }
  }

  return changes;
}

/**
 * 要素名を取得
 */
function getElementName(element: ReportElementUnion): string | undefined {
  // title プロパティがある要素
  if ('title' in element && typeof element.title === 'string') {
    return element.title;
  }
  return undefined;
}

/**
 * オブジェクト間で変更されたパスを取得
 */
function getChangedPaths(original: unknown, modified: unknown): string[] {
  const delta = diff(original, modified);
  if (!delta) return [];
  return extractChangedPaths(delta);
}

/**
 * 要素のプロパティ変更を取得
 */
function getElementPropertyChanges(
  original: ReportElementUnion,
  modified: ReportElementUnion
): PropertyChange[] {
  const changedPaths = getChangedPaths(original, modified);
  const changes: PropertyChange[] = [];

  for (const path of changedPaths) {
    // 一般的な比較対象外のプロパティをスキップ
    if (path === 'lastModified' || path === 'createdAt') continue;

    const oldValue = getNestedValue(original, path);
    const newValue = getNestedValue(modified, path);

    changes.push({
      property: path,
      oldValue,
      newValue,
    });
  }

  return changes;
}

/**
 * ネストしたオブジェクトから値を取得
 */
function getNestedValue(obj: unknown, path: string): unknown {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;

    // 配列インデックスのパース（例: 'layers[0]'）
    const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const index = parseInt(arrayMatch[2], 10);
      current = (current as Record<string, unknown>)[arrayKey];
      if (Array.isArray(current)) {
        current = current[index];
      } else {
        return undefined;
      }
    } else {
      current = (current as Record<string, unknown>)[key];
    }
  }

  return current;
}

/**
 * 設定の変更を取得
 */
function getSettingsChanges(
  original: Report['reportSettings'],
  modified: Report['reportSettings']
): PropertyChange[] {
  const changedPaths = getChangedPaths(original, modified);
  const changes: PropertyChange[] = [];

  for (const path of changedPaths) {
    const oldValue = getNestedValue(original, path);
    const newValue = getNestedValue(modified, path);

    changes.push({
      property: path,
      oldValue,
      newValue,
    });
  }

  return changes;
}
